import type {
  GenerationNotificationDiagnostic,
  GenerationNotificationDiagnosticsDto,
  GenerationNotificationLegacyReconciliationDto,
  GenerationNotificationRecoveryDto,
  GenerationNotificationStatus,
} from "dutyon-server/types";
import {
  type OrganizationGpuSessionStore,
  withGenerationSession,
} from "./organization-gpu-session";

const uuid = (value: unknown): value is string =>
  typeof value === "string" &&
  /^[a-f0-9]{8}-[a-f0-9]{4}-4[a-f0-9]{3}-[89ab][a-f0-9]{3}-[a-f0-9]{12}$/i.test(value);
const record = (v: unknown): v is Record<string, unknown> =>
  !!v && typeof v === "object" && !Array.isArray(v);
const date = (v: unknown): v is string =>
  typeof v === "string" && Number.isFinite(Date.parse(v)) && new Date(v).toISOString() === v;
const reference = (v: unknown) =>
  v === null || (typeof v === "string" && /^[A-Za-z0-9_-]{1,200}$/.test(v));
const count = (v: unknown): v is number =>
  Number.isSafeInteger(v) && Number(v) >= 0 && Number(v) <= 4;
const oneOf = (value: unknown, values: readonly string[]): value is string =>
  typeof value === "string" && values.includes(value);
const statuses = ["PENDING", "LEASED", "RETRY", "SENT", "UNKNOWN", "BLOCKED"];
export type GenerationDiagnosticsQuery = { status: GenerationNotificationStatus; cursor?: string };
function invalid(): never {
  throw new Error("생성 알림 응답을 확인하지 못했습니다.");
}
/** Select only known metadata; never propagate extra provider/body/credential fields. */
export function readGenerationDiagnostics(
  value: unknown,
  query: GenerationDiagnosticsQuery,
): GenerationNotificationDiagnosticsDto {
  if (
    !record(value) ||
    !oneOf(value.environment, ["development", "production"]) ||
    value.status !== query.status ||
    !uuid(value.auditId) ||
    !Array.isArray(value.items) ||
    value.items.length > 20 ||
    (value.nextCursor !== null && !uuid(value.nextCursor))
  )
    return invalid();
  const items = value.items.map((v): GenerationNotificationDiagnostic => {
    if (
      !record(v) ||
      !uuid(v.id) ||
      v.id === query.cursor ||
      v.status !== query.status ||
      !oneOf(v.kind, [
        "REQUEST",
        "ACCEPTED",
        "SUCCEEDED",
        "FAILED",
        "COMPLETED",
        "CANCELLED",
        "EXPIRED",
        "UNKNOWN",
      ]) ||
      !oneOf(v.slot, ["ACCEPTED", "FINAL"]) ||
      !oneOf(v.role, ["PARENT", "REPLY"]) ||
      !oneOf(v.app, ["dashboard", "backoffice", "pro", "mini", "manual", "unknown"]) ||
      !oneOf(v.sourceKind, ["MANUAL", "ORGANIZATION", "PRO", "MINI", "unknown"]) ||
      !reference(v.sourceId) ||
      !reference(v.businessRequestId) ||
      !date(v.acceptedAt) ||
      (v.finishedAt !== null && !date(v.finishedAt)) ||
      !date(v.createdAt) ||
      !date(v.nextAttemptAt) ||
      !count(v.attemptCount) ||
      typeof v.parentConfirmed !== "boolean" ||
      typeof v.waitingForParent !== "boolean" ||
      v.waitingForParent !== (v.role === "REPLY" && !v.parentConfirmed) ||
      typeof v.candidateLookupAvailable !== "boolean"
    )
      return invalid();
    const a = v.latestAttempt;
    if (
      a !== null &&
      (!record(a) ||
        !uuid(a.id) ||
        !count(a.ordinal) ||
        a.ordinal !== v.attemptCount ||
        a.ordinal < 1 ||
        !oneOf(a.state, ["IN_FLIGHT", "ACKNOWLEDGED", "REJECTED", "UNKNOWN"]) ||
        !date(a.createdAt) ||
        (a.acknowledgedAt !== null && !date(a.acknowledgedAt)) ||
        typeof a.wireProofAvailable !== "boolean")
    )
      return invalid();
    if (
      (a === null && v.attemptCount !== 0) ||
      (v.candidateLookupAvailable &&
        (v.status !== "UNKNOWN" || !record(a) || a.state !== "UNKNOWN" || !a.wireProofAvailable))
    )
      return invalid();
    const error = v.lastError;
    if (
      error !== null &&
      (!record(error) || typeof error.code !== "string" || !/^SLACK_[A-Z_]{1,80}$/.test(error.code))
    )
      return invalid();
    const row = v as unknown as GenerationNotificationDiagnostic;
    return {
      id: row.id,
      status: row.status,
      kind: row.kind,
      slot: row.slot,
      role: row.role,
      app: row.app,
      sourceKind: row.sourceKind,
      sourceId: row.sourceId,
      businessRequestId: row.businessRequestId,
      acceptedAt: row.acceptedAt,
      finishedAt: row.finishedAt,
      createdAt: row.createdAt,
      nextAttemptAt: row.nextAttemptAt,
      attemptCount: row.attemptCount,
      parentConfirmed: row.parentConfirmed,
      waitingForParent: row.waitingForParent,
      candidateLookupAvailable: row.candidateLookupAvailable,
      lastError: row.lastError
        ? { code: row.lastError.code, message: "알림 오류를 확인해 주세요." }
        : null,
      latestAttempt: row.latestAttempt
        ? {
            id: row.latestAttempt.id,
            ordinal: row.latestAttempt.ordinal,
            state: row.latestAttempt.state,
            createdAt: row.latestAttempt.createdAt,
            acknowledgedAt: row.latestAttempt.acknowledgedAt,
            wireProofAvailable: row.latestAttempt.wireProofAvailable,
          }
        : null,
    };
  });
  if (
    new Set(items.map((v) => v.id)).size !== items.length ||
    (value.nextCursor !== null &&
      (items.length !== 20 ||
        value.nextCursor !== items[items.length - 1]?.id ||
        value.nextCursor === query.cursor))
  )
    return invalid();
  return {
    environment: value.environment as GenerationNotificationDiagnosticsDto["environment"],
    status: query.status,
    auditId: value.auditId,
    nextCursor: value.nextCursor as string | null,
    items,
  };
}
export function readGenerationRecovery(
  value: unknown,
  eventId: string,
): GenerationNotificationRecoveryDto {
  if (
    !record(value) ||
    !uuid(value.operationId) ||
    value.eventId !== eventId ||
    !oneOf(value.status, ["recovered", "already_confirmed", "unconfirmed"]) ||
    (value.status === "unconfirmed" &&
      (typeof value.code !== "string" || !/^SLACK_[A-Z_]{1,80}$/.test(value.code))) ||
    (value.retryAfterMs !== undefined &&
      (value.status !== "unconfirmed" ||
        value.code !== "SLACK_HISTORY_RATE_LIMITED" ||
        !Number.isSafeInteger(value.retryAfterMs) ||
        Number(value.retryAfterMs) < 1000 ||
        Number(value.retryAfterMs) > 86400000))
  )
    return invalid();
  return {
    operationId: value.operationId,
    eventId,
    status: value.status as GenerationNotificationRecoveryDto["status"],
    ...(value.status === "unconfirmed" ? { code: value.code as string } : {}),
    ...(value.retryAfterMs !== undefined ? { retryAfterMs: value.retryAfterMs as number } : {}),
  };
}
export function readGenerationLegacyReconciliation(
  value: unknown,
  eventId: string,
): GenerationNotificationLegacyReconciliationDto {
  if (
    !record(value) ||
    !uuid(value.operationId) ||
    value.eventId !== eventId ||
    !oneOf(value.status, ["reconciled", "already_confirmed", "unconfirmed"]) ||
    (value.status === "unconfirmed" &&
      (typeof value.code !== "string" || !/^SLACK_[A-Z_]{1,80}$/.test(value.code))) ||
    value.retryAfterMs !== undefined
  )
    return invalid();
  return {
    operationId: value.operationId,
    eventId,
    status: value.status as GenerationNotificationLegacyReconciliationDto["status"],
    ...(value.status === "unconfirmed" ? { code: value.code as string } : {}),
  };
}
type Config = {
  headers: { Authorization: string };
  _retry: true;
  signal: AbortSignal;
  timeout: number;
  params?: GenerationDiagnosticsQuery;
};
type Transport = {
  get(path: string, config: Config): Promise<{ data: unknown }>;
  post(path: string, body: unknown, config: Config): Promise<{ data: unknown }>;
};
export function createGenerationNotificationsClient(
  session: OrganizationGpuSessionStore,
  transport: Transport,
) {
  return {
    reconcileLegacy(owner: string, eventId: string, signal?: AbortSignal) {
      if (!uuid(eventId)) return Promise.reject(new Error("기존 요청 알림을 확인해 주세요."));
      return withGenerationSession(
        session,
        owner,
        signal,
        async (config) =>
          readGenerationLegacyReconciliation(
            (
              await transport.post(
                "/admin/generation-notifications/reconcile-legacy",
                { eventId },
                { ...config, timeout: 15000 },
              )
            ).data,
            eventId,
          ),
        ["SUPER_ADMIN"],
      );
    },
    list(owner: string, query: GenerationDiagnosticsQuery, signal?: AbortSignal) {
      if (
        !statuses.includes(query.status) ||
        (query.cursor !== undefined && !uuid(query.cursor)) ||
        Object.keys(query).some((key) => !["status", "cursor"].includes(key))
      )
        return Promise.reject(new Error("조회 조건을 확인해 주세요."));
      const params = { status: query.status, ...(query.cursor ? { cursor: query.cursor } : {}) };
      return withGenerationSession(
        session,
        owner,
        signal,
        async (config) =>
          readGenerationDiagnostics(
            (
              await transport.get("/admin/generation-notifications", {
                ...config,
                params,
                timeout: 15000,
              })
            ).data,
            params,
          ),
        ["SUPER_ADMIN"],
      );
    },
    recover(owner: string, input: { eventId: string; candidateTs: string }, signal?: AbortSignal) {
      if (
        !uuid(input.eventId) ||
        typeof input.candidateTs !== "string" ||
        !/^\d{10,16}\.\d{6}$/.test(input.candidateTs) ||
        Object.keys(input).some((key) => !["eventId", "candidateTs"].includes(key))
      )
        return Promise.reject(new Error("후보 메시지를 확인해 주세요."));
      const body = { eventId: input.eventId, candidateTs: input.candidateTs };
      return withGenerationSession(
        session,
        owner,
        signal,
        async (config) =>
          readGenerationRecovery(
            (
              await transport.post("/admin/generation-notifications/recover", body, {
                ...config,
                timeout: 25000,
              })
            ).data,
            body.eventId,
          ),
        ["SUPER_ADMIN"],
      );
    },
  };
}
